import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Category, Product } from "@shared/types";
import { api } from "@/lib/api";
import { StorefrontLayout } from "@/components/StorefrontLayout";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/Button";

export function Home() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const arrivalsRef = useRef<HTMLElement>(null);

  const scrollToArrivals = () => {
    setActiveCategory(null);
    arrivalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    api.categories.list().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.products
      .list(activeCategory ? { category: activeCategory } : undefined)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <StorefrontLayout>
      <section className="relative flex h-[460px] w-full flex-col justify-end overflow-hidden bg-camel">
        <img
          src="/hero.jpg"
          alt=""
          className="absolute inset-0 h-full w-full grayscale object-cover object-[78%_center]"
        />
        {/* Left-to-right scrim for text legibility over the model, fading into the cream section below */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-cream to-transparent" />
        <div className="relative z-10 flex flex-col gap-4 px-6 pb-10">
          <span className="text-[11px] font-semibold uppercase tracking-[2px] text-white">New Season</span>
          <h1 className="font-display text-[32px] font-semibold leading-[1.12] text-white">
            Carry it
            <br />
            with confidence
          </h1>
          <p className="max-w-[280px] text-sm leading-relaxed text-white/85">
            Handcrafted leather handbags for the woman who defines her own style.
          </p>
          <Button variant="secondary" className="w-fit" onClick={scrollToArrivals}>
            Shop New Arrivals
          </Button>
        </div>
      </section>

      <div className="no-scrollbar flex gap-2 overflow-x-auto bg-cream px-5 pb-2 pt-5">
        <CategoryChip label="All" active={activeCategory === null} onClick={() => setActiveCategory(null)} />
        {categories.map((c) => (
          <CategoryChip
            key={c.id}
            label={c.name}
            active={activeCategory === c.slug}
            onClick={() => setActiveCategory(c.slug)}
          />
        ))}
      </div>

      <section ref={arrivalsRef} className="scroll-mt-16 px-5 pb-8 pt-2">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-medium text-ink">New Arrivals</h2>
          <Link to="/search" className="text-xs font-semibold text-stone-700">
            View all
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[164/277] animate-pulse rounded-xl bg-stone-100" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="py-12 text-center text-sm text-stone-500">No products here yet — check back soon.</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </StorefrontLayout>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "shrink-0 rounded-pill px-4 py-2 text-[13px] font-semibold transition",
        active ? "bg-ink text-white" : "border border-stone-300 bg-white text-ink",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
